import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { Header } from '../components/Header';
import Seo from '../components/Seo';
import { postsByDate as blogPosts, lastModified } from '../data/blogPosts';

const Blog = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'My Football Game Blog',
    url: 'https://www.myfootballgame.com/blog',
    description: 'Guides, tactics and tips for My Football Game managers.',
    blogPost: blogPosts.map((post) => ({
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.description,
      datePublished: post.date,
      dateModified: lastModified(post),
      url: `https://www.myfootballgame.com/blog/${post.slug}`
    }))
  };

  return (
    <motion.div
      className="w-full min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Seo
        title="Blog - Manager Guides, Tactics & Tips | My Football Game"
        description="Guides and strategy for My Football Game managers: beginner tips, tactics deep dives and transfer market strategy to help you climb the leaderboard."
        path="/blog"
        jsonLd={jsonLd}
      />
      <div className="container mx-auto px-4">
        <Header />
      </div>

      <div className="max-w-4xl mx-auto px-4 pb-16">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-10"
        >
          <div className="pc-eyebrow pc-eyebrow-accent mb-3">Manager Guides</div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">My Football Game Blog</h1>
          <p className="text-white/70">
            Tactics, transfer strategy and tips from the touchline - everything you need to outmanage your rivals.
          </p>
        </motion.div>

        <div className="space-y-6">
          {blogPosts.map((post, index) => (
            <motion.article
              key={post.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(0.3 + index * 0.05, 0.8) }}
              className="bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-lg p-6 shadow-md hover:bg-white/[0.05] transition-colors"
            >
              <div className="text-xs text-white/50 mb-2">
                {new Date(post.date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} · {post.readTime}
              </div>
              <h2 className="text-xl md:text-2xl font-bold mb-2">
                <Link to={`/blog/${post.slug}`} className="hover:text-green-400 transition-colors">
                  {post.title}
                </Link>
              </h2>
              <p className="text-white/70 mb-4">{post.description}</p>
              <Link to={`/blog/${post.slug}`} className="text-green-400 hover:underline font-medium">
                Read the guide →
              </Link>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-10 text-center text-white/70"
        >
          New to the game? Start with{' '}
          <Link to="/how-to-play" className="text-green-400 hover:underline">How to Play</Link> or the{' '}
          <Link to="/faq" className="text-green-400 hover:underline">FAQ</Link>.
        </motion.div>
      </div>

      <Footer />
    </motion.div>
  );
};

export default Blog;
